import { WebSocketTransport } from './transport/WebSocketTransport';
import type { ITransport, TransportAvailabilityInfo, TransportCloseInfo } from './transport/ITransport';
import type { TransportConnectionInfo } from './transport/ITransport';
import { readMessage } from './message';
import { MessageTag } from './messageTags';
import type { BinaryReader } from './BinaryReader';
import {
  encodeLoadLogin,
  encodeLogin,
  decodeLoginResult,
  decodeServerMessage,
  type LoginRequest,
  type LoginResult,
} from './codec/login';
import { encodeReconnect, decodeReconnect, type ReconnectResult } from './codec/reconnect';

export type MessageHandler = (reader: BinaryReader) => void;
export type ClientErrorHandler = (error: unknown) => void;

interface PendingWaiter {
  connectionId: number;
  resolve: (reader: BinaryReader) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  signal?: AbortSignal;
  abortHandler?: () => void;
}

export interface WaitForOptions {
  connectionId?: number;
  timeoutMs: number;
  signal?: AbortSignal;
  timeoutMessage?: string;
}

/**
 * Thin session object over the transport: connect, dispatch inbound frames by MessageTag, and run the
 * login handshake. Game scenes (lobby, Blackjack table) subscribe with on()/once() and send encoded
 * frames with send(). This is the single place that knows about the wire — scenes stay presentational.
 */
export class GameClient {
  private readonly transport: ITransport;
  private readonly handlers = new Map<MessageTag, Set<MessageHandler>>();
  private readonly onceHandlers = new Map<MessageTag, Set<MessageHandler>>();
  private readonly waiters = new Map<MessageTag, Set<PendingWaiter>>();
  private readonly openHandlers = new Set<(info: TransportConnectionInfo) => void>();
  private readonly closeHandlers = new Set<(info: TransportCloseInfo) => void>();
  private readonly availabilityHandlers = new Set<(info: TransportAvailabilityInfo) => void>();
  private readonly errorHandlers = new Set<ClientErrorHandler>();
  private _url = '';
  private _connectionId: number | null = null;
  private loadLoginConnectionId: number | null = null;

  constructor(transport: ITransport = new WebSocketTransport()) {
    this.transport = transport;
    this._connectionId = transport.connectionId;
    this.transport.onOpen((info) => {
      this._connectionId = info.connectionId;
      this.loadLoginConnectionId = null;
      for (const handler of [...this.openHandlers]) handler(info);
    });
    this.transport.onMessage((data, connectionId) => {
      if (connectionId === this._connectionId) this.dispatch(data, connectionId);
    });
    this.transport.onClose((info) => {
      this.rejectWaitersForConnection(info.connectionId, new Error(info.reason || 'Connection closed'));
      if (info.current && this._connectionId === info.connectionId) {
        this._connectionId = null;
        this.loadLoginConnectionId = null;
      }
      for (const handler of [...this.closeHandlers]) handler(info);
    });
    this.transport.onAvailability((info) => {
      if (info.connectionId !== this._connectionId) return;
      if (info.state === 'suspended') {
        this.loadLoginConnectionId = null;
        this.rejectWaitersForConnection(info.connectionId, new Error('Game server is restarting'));
      }
      for (const handler of [...this.availabilityHandlers]) handler(info);
    });
    this.transport.onError((error) => {
      for (const handler of [...this.errorHandlers]) handler(error);
    });
  }

  get isOpen(): boolean {
    return this.transport.isOpen;
  }

  get isReady(): boolean {
    return this.transport.isReady;
  }

  /** The last URL passed to connect() — used to reconnect after a drop. */
  get url(): string {
    return this._url;
  }

  get connectionId(): number | null {
    return this._connectionId;
  }

  get protocol(): string {
    return this.transport.protocol;
  }

  async connect(url: string): Promise<TransportConnectionInfo> {
    this._url = url;
    const info = await this.transport.connect(url);
    // Some deterministic test transports resolve without emitting open.
    if (this._connectionId !== info.connectionId) {
      this._connectionId = info.connectionId;
      this.loadLoginConnectionId = null;
    }
    return info;
  }

  close(): void {
    this.transport.close();
  }

  faultConnection(code = 4010, reason = 'request_timeout'): void {
    this.transport.close(code, reason, false);
  }

  probe(): void {
    this.transport.probe();
  }

  onOpen(handler: (info: TransportConnectionInfo) => void): () => void {
    this.openHandlers.add(handler);
    return () => this.openHandlers.delete(handler);
  }

  onClose(handler: (info: TransportCloseInfo) => void): () => void {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  onAvailability(handler: (info: TransportAvailabilityInfo) => void): () => void {
    this.availabilityHandlers.add(handler);
    return () => this.availabilityHandlers.delete(handler);
  }

  onError(handler: ClientErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  send(bytes: Uint8Array): void {
    this.transport.send(bytes);
  }

  /**
   * Ask the server whether the gateway-verified Telegram identity is already bound to an account.
   * The placeholder never reaches the game server: the gateway replaces it with TG:<user id>.
   */
  loadLogin(): void {
    if (!this.isReady || this._connectionId === null || this.loadLoginConnectionId === this._connectionId) return;
    this.send(encodeLoadLogin('WEB:gateway-must-rewrite'));
    this.loadLoginConnectionId = this._connectionId;
  }

  /** Subscribe to every frame with this tag. Returns an unsubscribe function. */
  on(tag: MessageTag, handler: MessageHandler): () => void {
    let set = this.handlers.get(tag);
    if (!set) this.handlers.set(tag, (set = new Set()));
    set.add(handler);
    return () => set!.delete(handler);
  }

  /** Subscribe to the next frame with this tag only. */
  once(tag: MessageTag, handler: MessageHandler): () => void {
    let set = this.onceHandlers.get(tag);
    if (!set) this.onceHandlers.set(tag, (set = new Set()));
    set.add(handler);
    return () => set!.delete(handler);
  }

  waitFor(tag: MessageTag, options: WaitForOptions): Promise<BinaryReader> {
    const connectionId = options.connectionId ?? this._connectionId;
    if (connectionId === null) return Promise.reject(new Error('Connection is not open'));
    const existing = this.waiters.get(tag);
    if (existing && [...existing].some((waiter) => waiter.connectionId === connectionId)) {
      return Promise.reject(new Error(`A ${MessageTag[tag]} request is already pending on this connection`));
    }

    return new Promise<BinaryReader>((resolve, reject) => {
      const waiter: PendingWaiter = {
        connectionId,
        resolve,
        reject,
        signal: options.signal,
        timer: setTimeout(() => {
          this.removeWaiter(tag, waiter);
          reject(new Error(options.timeoutMessage ?? `${MessageTag[tag]} timed out`));
        }, options.timeoutMs),
      };
      waiter.abortHandler = () => {
        this.removeWaiter(tag, waiter);
        reject(new Error('Request cancelled'));
      };
      if (options.signal?.aborted) {
        clearTimeout(waiter.timer);
        reject(new Error('Request cancelled'));
        return;
      }
      options.signal?.addEventListener('abort', waiter.abortHandler, { once: true });
      let set = this.waiters.get(tag);
      if (!set) this.waiters.set(tag, (set = new Set()));
      set.add(waiter);
    });
  }

  private dispatch(data: Uint8Array, connectionId: number): void {
    let tag: MessageTag;
    try {
      ({ tag } = readMessage(data));
    } catch (error) {
      for (const handler of [...this.errorHandlers]) handler(error);
      this.faultConnection(1002, 'malformed_server_frame');
      return;
    }
    // Each handler gets its own reader positioned at the payload, so multiple subscribers don't
    // fight over a shared cursor.
    const freshReader = (): BinaryReader => readMessage(data).reader;

    const waiters = this.waiters.get(tag);
    const waiter = waiters && [...waiters].find((candidate) => candidate.connectionId === connectionId);
    if (waiter) {
      this.removeWaiter(tag, waiter);
      waiter.resolve(freshReader());
    }

    const once = this.onceHandlers.get(tag);
    if (once && once.size) {
      this.onceHandlers.set(tag, new Set());
      for (const handler of once) this.invokeHandler(handler, freshReader());
    }
    const set = this.handlers.get(tag);
    if (set && set.size) for (const handler of [...set]) this.invokeHandler(handler, freshReader());
  }

  /**
   * Full login handshake: LoadLogin (the gateway replaces its value with the verified Telegram ID)
   * then Login. Resolves with the decoded
   * success packet, or rejects with the server's rejection text (delivered as MessageTag.SendMessage).
   */
  login(req: LoginRequest, timeoutMs = 15_000): Promise<LoginResult> {
    return this.runLogin(req, timeoutMs);
  }

  private async runLogin(req: LoginRequest, timeoutMs: number): Promise<LoginResult> {
    const connectionId = this.requireConnection();
    const cancellation = new AbortController();
    const login = this.waitFor(MessageTag.Login, {
      connectionId,
      timeoutMs,
      signal: cancellation.signal,
      timeoutMessage: 'Login timed out — is the server reachable?',
    }).then(decodeLoginResult);
    const failure = this.waitFor(MessageTag.SendMessage, {
      connectionId,
      timeoutMs,
      signal: cancellation.signal,
      timeoutMessage: 'Login timed out — is the server reachable?',
    }).then((reader) => { throw new Error(decodeServerMessage(reader)); });
    const outcome = Promise.race([login, failure]);
    // Sending can fail synchronously (for example, browser backpressure). Keep the already-created
    // waiter race observed even when execution never reaches the await below.
    void outcome.catch(() => undefined);

    try {
      this.loadLogin();
      this.send(encodeLogin(req));
      return await outcome;
    } catch (error) {
      if (this._connectionId === connectionId && /timed out/i.test(String(error))) {
        this.faultConnection(4010, 'login_timeout');
      }
      throw error;
    } finally {
      cancellation.abort();
    }
  }

  /**
   * Reconnect handshake: re-register the dropped player with the saved token (no password). Resolves with
   * the decoded {success, playingGame, …} reply. Rejects on timeout or connection loss. The caller
   * (ReconnectController) treats transient failures as retryable. Unlike login(), this does NOT listen
   * for SendMessage(0) — the only meaningful reply here is Reconnect(76) or silence — so an unrelated admin
   * popup can't abort a reconnect.
   */
  reconnect(token: string, timeoutMs = 8_000): Promise<ReconnectResult> {
    return this.runReconnect(token, timeoutMs);
  }

  private async runReconnect(token: string, timeoutMs: number): Promise<ReconnectResult> {
    const connectionId = this.requireConnection();
    const cancellation = new AbortController();
    const response = this.waitFor(MessageTag.Reconnect, {
      connectionId,
      timeoutMs,
      signal: cancellation.signal,
      timeoutMessage: 'Reconnect timed out',
    });
    // As above, LoadLogin/Reconnect sends can throw before response is awaited.
    void response.catch(() => undefined);
    try {
      // Ordered immediately before Reconnect so the server can bind TG reconnect tokens to the
      // gateway-verified identity without changing native Android reconnect packets.
      this.loadLogin();
      this.send(encodeReconnect(token));
      return decodeReconnect(await response);
    } catch (error) {
      if (this._connectionId === connectionId && /timed out/i.test(String(error))) {
        this.faultConnection(4010, 'reconnect_timeout');
      }
      throw error;
    } finally {
      cancellation.abort();
    }
  }

  private requireConnection(): number {
    if (!this.isReady || this._connectionId === null) throw new Error('Game server is temporarily unavailable');
    return this._connectionId;
  }

  private removeWaiter(tag: MessageTag, waiter: PendingWaiter): void {
    clearTimeout(waiter.timer);
    if (waiter.abortHandler) waiter.signal?.removeEventListener('abort', waiter.abortHandler);
    const set = this.waiters.get(tag);
    set?.delete(waiter);
    if (set?.size === 0) this.waiters.delete(tag);
  }

  private rejectWaitersForConnection(connectionId: number, error: Error): void {
    for (const [tag, waiters] of this.waiters) {
      for (const waiter of [...waiters]) {
        if (waiter.connectionId !== connectionId) continue;
        this.removeWaiter(tag, waiter);
        waiter.reject(error);
      }
    }
  }

  private invokeHandler(handler: MessageHandler, reader: BinaryReader): void {
    try {
      handler(reader);
    } catch (error) {
      for (const errorHandler of [...this.errorHandlers]) errorHandler(error);
      this.faultConnection(1002, 'malformed_server_payload');
    }
  }
}
